import { Injectable } from '@angular/core';
import { createStore, select, setProps, withProps } from '@ngneat/elf';
import {
  addEntities,
  deleteAllEntities,
  entitiesPropsFactory,
  getAllEntities,
  selectAllEntities,
  setEntities,
  updateEntitiesByPredicate,
  withActiveId,
  withEntities,
} from '@ngneat/elf-entities';
import { persistState } from '@ngneat/elf-persist-state';
import { createRequestDataSource, createRequestsStatusOperator, initializeAsPending, updateRequestStatus, withRequestsStatus } from '@ngneat/elf-requests';
import { GetOrdersResultExternal, PreparationStatutCommandes, TicketDto } from '@timechef/core/api';
import { AppErrorKeys, appErrorFromKey } from '@timechef/core/errors/error';
import { ensureDate } from '@timechef/core/utils';
import { capacitorStorageStrategy, includeKeys, selectAppRequestStatus } from '@timechef/core/utils/elf-utils';
import { isSameDay } from 'date-fns';
import { map } from 'rxjs';
import { Order, PageInfo, PageInfoWithData } from './history.models';
import { computeHistory } from './history.utils';

interface HistoryProps {
  ticketsPageInfo: PageInfo;
  ordersPageInfo: PageInfo;
  externalOrders: GetOrdersResultExternal | null;
  standaloneOrder: Order | null;
  isInstallingScannerModule: boolean;
}

export enum HistoryStatusKeys {
  Tickets = 'tickets',
  Orders = 'orders',
  GeneratePdf = 'generate-pdf',
  GeneratePdfBatch = 'generate-pdf-batch',
  StandaloneOrder = 'standalone-order',
}

@Injectable({ providedIn: 'root' })
export class HistoryRepository {
  ordersFactory = entitiesPropsFactory('orders');

  private store = createStore(
    { name: 'history' },
    withProps<HistoryProps>({
      ticketsPageInfo: {
        hasMore: false,
        currentPageIndex: 0,
      },
      ordersPageInfo: {
        hasMore: false,
        currentPageIndex: 0,
      },
      standaloneOrder: null,
      externalOrders: null,
      isInstallingScannerModule: false,
    }),
    withEntities<TicketDto>(),
    this.ordersFactory.withOrdersEntities<Order>(),
    withActiveId(),
    withRequestsStatus(initializeAsPending([HistoryStatusKeys.Tickets, HistoryStatusKeys.Orders])),
    withRequestsStatus<HistoryStatusKeys.GeneratePdf>(),
  );

  instance = persistState(this.store, {
    key: 'history',
    storage: capacitorStorageStrategy,
    source: () => this.store.pipe(includeKeys(['entities', 'ids', 'activeId', 'ordersEntities', 'ordersIds'])),
  });

  cancelOrderStatus$ = (orderId: number) => this.store.pipe(selectAppRequestStatus(buildCancelOrderKey(orderId)));
  updateTableNumberStatus$ = (orderId: number) => this.store.pipe(selectAppRequestStatus(buildTableNumberKey(orderId)));
  generatePdfRequestStatus$ = this.store.pipe(selectAppRequestStatus(HistoryStatusKeys.GeneratePdf));
  generatePdfBatchRequestStatus$ = this.store.pipe(selectAppRequestStatus(HistoryStatusKeys.GeneratePdfBatch));

  ticketsByPeriodDS = createRequestDataSource({
    store: this.store,
    data$: () =>
      this.store.pipe(
        selectAllEntities(),
        map((tickets) => computeHistory(new Date(), tickets)),
      ),
    requestKey: HistoryStatusKeys.Tickets,
    dataKey: 'data',
    idleAsPending: true,
  });

  standaloneOrderDS = createRequestDataSource({
    store: this.store,
    data$: () => this.store.pipe(select((s) => s.standaloneOrder)),
    requestKey: HistoryStatusKeys.StandaloneOrder,
    dataKey: 'data',
    idleAsPending: false,
  });

  ticketsPageInfo$ = this.store.pipe(select((s) => s.ticketsPageInfo));
  ticketsPageInfoWithData$ = this.store.pipe(
    select((s) => {
      const pageInfoWithData: PageInfoWithData<TicketDto> = {
        currentPageIndex: s.ticketsPageInfo.currentPageIndex,
        hasMore: s.ticketsPageInfo.hasMore,
        data: s.ids.map((id) => s.entities[id]),
      };

      return pageInfoWithData;
    }),
  );

  ordersByPeriodDS = createRequestDataSource({
    store: this.store,
    data$: () =>
      this.store.pipe(
        selectAllEntities({ ref: this.ordersFactory.ordersEntitiesRef }),
        map((orders) => computeHistory(new Date(), orders)),
      ),
    requestKey: HistoryStatusKeys.Orders,
    dataKey: 'data',
    idleAsPending: true,
  });

  externalOrders$ = this.store.pipe(select((s) => s.externalOrders));
  ordersPageInfo$ = this.store.pipe(select((s) => s.ordersPageInfo));
  ordersPageInfoWithData$ = this.store.pipe(
    select((s) => {
      const pageInfoWithData: PageInfoWithData<Order> = {
        currentPageIndex: s.ordersPageInfo.currentPageIndex,
        hasMore: s.ordersPageInfo.hasMore,
        data: s.ordersIds.map((id) => s.ordersEntities[id]),
      };

      return pageInfoWithData;
    }),
  );
  isInstallingScannerModule$ = this.store.pipe(select((s) => s.isInstallingScannerModule));

  getTicketsRequestStatus = () => this.store.query((s) => s.requestsStatus[HistoryStatusKeys.Tickets]);
  getTicketByIndex = (idx: number) => this.store.query(getAllEntities())[idx];
  addTickets(tickets: TicketDto[], hasMore: boolean, currentPageIndex: number) {
    const clear = currentPageIndex === 0;
    this.store.update(
      setProps({ ticketsPageInfo: { hasMore, currentPageIndex } }),
      clear ? setEntities(tickets) : addEntities(tickets),
      this.ticketsByPeriodDS.setSuccess(),
    );
  }
  getPaginatedTickets(): PageInfoWithData<TicketDto> {
    const entities = this.store.query(getAllEntities());
    return this.store.query((s) => {
      return {
        hasMore: s.ticketsPageInfo.hasMore,
        currentPageIndex: s.ticketsPageInfo.currentPageIndex,
        data: entities,
      };
    });
  }

  getOrdersRequestStatus = () => this.store.query((s) => s.requestsStatus[HistoryStatusKeys.Orders]);

  getOrderByIndex = (idx: number) => this.store.query(getAllEntities({ ref: this.ordersFactory.ordersEntitiesRef }))[idx];

  getSingleScorpOrderWithDate = (date: Date) => {
    const orders = this.store.query(getAllEntities({ ref: this.ordersFactory.ordersEntitiesRef }));

    return orders.find((o) => o.scorp && o.collectionDate && isSameDay(ensureDate(o.collectionDate), date));
  };

  addOrders(orders: Order[], hasMore: boolean, currentPageIndex: number) {
    const clear = currentPageIndex === 0;
    this.store.update(
      setProps({ ordersPageInfo: { hasMore, currentPageIndex } }),
      clear ? setEntities(orders, { ref: this.ordersFactory.ordersEntitiesRef }) : addEntities(orders, { ref: this.ordersFactory.ordersEntitiesRef }),
      this.ordersByPeriodDS.setSuccess(),
      (s) => ({ ...s, externalOrders: null }),
    );
  }

  setStandaloneOrder(order: Order) {
    this.store.update(setProps({ standaloneOrder: order }), this.standaloneOrderDS.setSuccess());
  }

  setExternalOrders(externalOrders: GetOrdersResultExternal) {
    this.store.update((s) => ({ ...s, externalOrders }), deleteAllEntities({ ref: this.ordersFactory.ordersEntitiesRef }), this.ordersByPeriodDS.setSuccess());
  }

  getPaginatedOrders(): PageInfoWithData<Order> {
    const entities = this.store.query(getAllEntities({ ref: this.ordersFactory.ordersEntitiesRef }));
    return this.store.query((s) => {
      return {
        hasMore: s.ordersPageInfo.hasMore,
        currentPageIndex: s.ordersPageInfo.currentPageIndex,
        data: entities,
      };
    });
  }
  setIsInstallingScannerModule(isInstallingScannerModule: boolean) {
    this.store.update(setProps({ isInstallingScannerModule }));
  }

  resetUpdateTableRequestStatus = (orderId: number) => this.store.update(updateRequestStatus(buildTableNumberKey(orderId), 'idle'));
  setErrorUpdateTableRequestStatus = (orderId: number, errorKey: AppErrorKeys | string) =>
    this.store.update(updateRequestStatus(buildTableNumberKey(orderId), 'error', appErrorFromKey(errorKey)));

  setCancelOrderSuccess = (orderId: number) => this.store.update(updateRequestStatus(buildCancelOrderKey(orderId), 'success'));

  updateTableNumber = (orderId: number, tableNumber?: number) => {
    this.store.update(
      (s) => ({ ...s, standaloneOrder: s.standaloneOrder?.id === orderId ? { ...s.standaloneOrder!, tableNumber } : s.standaloneOrder }),
      updateEntitiesByPredicate(
        (x) => x.id === orderId,
        (o) => ({
          ...o,
          tableNumber,
          preparationStatus:
            tableNumber !== undefined && o.preparationStatus === PreparationStatutCommandes.EnAttente
              ? PreparationStatutCommandes.APreparer
              : o.preparationStatus,
        }),
        { ref: this.ordersFactory.ordersEntitiesRef },
      ),
      updateRequestStatus(buildTableNumberKey(orderId), 'success'),
    );
  };
  setPdfAsGenerated = () => this.store.update(updateRequestStatus(HistoryStatusKeys.GeneratePdf, 'success'));
  setPdfBatchAsGenerated = () => this.store.update(updateRequestStatus(HistoryStatusKeys.GeneratePdfBatch, 'success'));
  public trackRequestStatus = createRequestsStatusOperator(this.store);
}

export const buildCancelOrderKey = (orderId: number): `cancel-order.${string}` => `cancel-order.${orderId}`;

export const buildTableNumberKey = (orderId: number): `update-table-number.${string}` => `update-table-number.${orderId}`;
